import { CSSProperties, useState, useEffect } from 'react'
import { AssetGenerationStatus } from 'Hooks/useMaestroChat'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Icon from 'elements/Icon'


const AssetGenerationInline = ({
    assetGeneration
}: {
    assetGeneration: {
        assetId: string
        assetType: 'video' | 'image'
        status: AssetGenerationStatus
        message: string
        estimatedTimeMinutes: number
        startTime: number
    }
}) => {
    const [isHovered, setIsHovered] = useState(false)
    const [elapsedTime, setElapsedTime] = useState(0)
    const [assetAspectRatio, setAssetAspectRatio] = useState<'square' | 'wide' | 'tall'>('wide')

    useEffect(() => {
        const timer = setInterval(() => {
            const elapsed = Math.floor((Date.now() - assetGeneration.startTime) / 1000)
            setElapsedTime(elapsed)
        }, 1000)

        return () => clearInterval(timer)
    }, [assetGeneration.startTime])

    // Detect aspect ratio when asset is completed
    useEffect(() => {
        if (assetGeneration.status?.status === 'completed' && assetGeneration.status?.resultMetadata) {
            const metadata = assetGeneration.status.resultMetadata

            if (assetGeneration.assetType === 'video' && metadata.thumbnailUrl) {
                const img = new Image()
                img.onload = () => {
                    const aspectRatio = img.naturalWidth / img.naturalHeight
                    if (aspectRatio > 1.5) {
                        setAssetAspectRatio('wide')
                    } else if (aspectRatio < 0.75) {
                        setAssetAspectRatio('tall')
                    } else {
                        setAssetAspectRatio('square')
                    }
                }
                img.src = metadata.thumbnailUrl
            } else if (assetGeneration.assetType === 'image' && metadata.images?.[0]) {
                const img = new Image()
                img.onload = () => {
                    const aspectRatio = img.naturalWidth / img.naturalHeight
                    if (aspectRatio > 1.5) {
                        setAssetAspectRatio('wide')
                    } else if (aspectRatio < 0.75) {
                        setAssetAspectRatio('tall')
                    } else {
                        setAssetAspectRatio('square')
                    }
                }
                img.src = metadata.images[0].cloudinaryUrl
            }
        }
    }, [assetGeneration.status, assetGeneration.assetType])

    const currentStatus = assetGeneration.status
    const progress = currentStatus?.progress || 0
    const statusMessage = currentStatus?.errorMessage || assetGeneration.message
    const isCompleted = currentStatus?.status === 'completed'
    const isFailed = currentStatus?.status === 'failed'
    const resultMetadata = currentStatus?.resultMetadata

    // Format elapsed time
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Get status icon and color
    const getStatusInfo = () => {
        if (!currentStatus) {
            return { icon: 'clock', color: Colors.greyDark }
        }

        switch (currentStatus.status) {
            case 'generating':
                return {
                    icon: assetGeneration.assetType === 'video' ? 'video' : 'image',
                    color: Colors.purple
                }
            case 'completed':
                return { icon: 'check', color: Colors.green }
            case 'failed':
                return { icon: 'warning', color: Colors.red }
            default:
                return { icon: 'clock', color: Colors.greyDark }
        }
    }

    const statusInfo = getStatusInfo()

    const handleAssetClick = () => {
        if (assetGeneration.assetType === 'video' && resultMetadata?.videoUrl) {
            window.open(resultMetadata.videoUrl, '_blank')
        } else if (assetGeneration.assetType === 'image' && resultMetadata?.images?.[0]) {
            window.open(resultMetadata.images[0].cloudinaryUrl, '_blank')
        }
    }

    // Completed state with asset preview
    if (isCompleted && resultMetadata) {
        return (
            <div
                className="assetGenerationInlineCompleted"
                style={styx(
                    sty.assetGenerationInlineCard,
                    sty.assetGenerationInlineCompleted,
                    sty[`assetGenerationInlineCompleted_${assetAspectRatio}`],
                    isHovered && sty.assetGenerationInlineCompleted_hover
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleAssetClick}
            >
                {/* Asset thumbnail with play/view overlay */}
                <div
                    className="assetGenerationInlineAssetContainer"
                    style={styx(
                        sty.assetGenerationInlineVideoContainer,
                        sty[`assetGenerationInlineVideoContainer_${assetAspectRatio}`]
                    )}
                >
                    {assetGeneration.assetType === 'video' ? (
                        <>
                            <img
                                src={resultMetadata.thumbnailUrl}
                                alt="Generated video thumbnail"
                                className="assetGenerationInlineAssetThumbnail"
                                style={sty.assetGenerationInlineVideoThumbnail}
                            />
                            <div className="assetGenerationInlinePlayOverlay" style={sty.assetGenerationInlinePlayOverlay}>
                                <Icon
                                    icon="play"
                                    size={24}
                                    className="assetGenerationInlinePlayIcon"
                                    style={sty.assetGenerationInlinePlayIcon}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <img
                                src={resultMetadata.images?.[0]?.cloudinaryUrl}
                                alt="Generated image"
                                className="assetGenerationInlineAssetThumbnail"
                                style={sty.assetGenerationInlineVideoThumbnail}
                            />
                            <div className="assetGenerationInlineViewOverlay" style={sty.assetGenerationInlinePlayOverlay}>
                                <Icon
                                    icon="externalLink"
                                    size={24}
                                    className="assetGenerationInlineViewIcon"
                                    style={sty.assetGenerationInlinePlayIcon}
                                />
                            </div>
                        </>
                    )}

                    {/* Success badge */}
                    <div className="assetGenerationInlineSuccessBadge" style={sty.assetGenerationInlineSuccessBadge}>
                        <Icon
                            icon="check"
                            size={16}
                            className="assetGenerationInlineSuccessIcon"
                            style={sty.assetGenerationInlineSuccessIcon}
                        />
                        <span className="assetGenerationInlineSuccessText" style={sty.assetGenerationInlineSuccessText}>
                            Ready!
                        </span>
                    </div>
                </div>

                {/* Asset info */}
                <div className="assetGenerationInlineAssetInfo" style={sty.assetGenerationInlineVideoInfo}>
                    <div className="assetGenerationInlineAssetTitle" style={sty.assetGenerationInlineVideoTitle}>
                        {assetGeneration.assetType === 'video' ? '🎬 Video Complete' : '🎨 Image Complete'}
                    </div>
                    <div className="assetGenerationInlineAssetSubtitle" style={sty.assetGenerationInlineVideoSubtitle}>
                        Click to view your generated {assetGeneration.assetType}
                        {assetGeneration.assetType === 'image' && resultMetadata.images && resultMetadata.images.length > 1
                            && ` (${resultMetadata.images.length} images)`}
                    </div>
                    <div className="assetGenerationInlineAssetStats" style={sty.assetGenerationInlineVideoStats}>
                        <Icon
                            icon="clock"
                            size={12}
                            className="assetGenerationInlineStatsIcon"
                        />
                        <span className="assetGenerationInlineStatsText" style={sty.assetGenerationInlineStatsText}>
                            Generated in {formatTime(elapsedTime)}
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    // Failed state
    if (isFailed) {
        return (
            <div
                className="assetGenerationInlineFailed"
                style={styx(
                    sty.assetGenerationInlineCard,
                    sty.assetGenerationInlineFailed,
                    isHovered && sty.assetGenerationInlineFailed_hover
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="assetGenerationInlineFailedIcon" style={sty.assetGenerationInlineFailedIcon}>
                    <Icon
                        icon="warning"
                        size={32}
                        className="assetGenerationInlineFailedIconSvg"
                        style={sty.assetGenerationInlineFailedIconSvg}
                    />
                </div>
                <div className="assetGenerationInlineFailedInfo" style={sty.assetGenerationInlineFailedInfo}>
                    <div className="assetGenerationInlineFailedTitle" style={sty.assetGenerationInlineFailedTitle}>
                        {assetGeneration.assetType === 'video' ? 'Video' : 'Image'} Generation Failed
                    </div>
                    <div className="assetGenerationInlineFailedSubtitle" style={sty.assetGenerationInlineFailedSubtitle}>
                        {statusMessage || 'Something went wrong. Please try again.'}
                    </div>
                </div>
            </div>
        )
    }

    // In-progress state (generating)
    return (
        <div
            className="assetGenerationInline"
            style={styx(
                sty.assetGenerationInlineCard,
                sty.assetGenerationInlineInProgress,
                isHovered && sty.assetGenerationInlineInProgress_hover
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Header with asset icon and title */}
            <div className="assetGenerationInlineHeader" style={sty.assetGenerationInlineHeader}>
                <div className="assetGenerationInlineTitleSection" style={sty.assetGenerationInlineTitleSection}>
                    <Icon
                        icon={assetGeneration.assetType === 'video' ? 'video' : 'image'}
                        size={20}
                        className="assetGenerationInlineIcon"
                        style={styx(sty.assetGenerationInlineIcon, { color: Colors.purple })}
                    />
                    <div className="assetGenerationInlineTitles" style={sty.assetGenerationInlineTitles}>
                        <div className="assetGenerationInlineTitle" style={sty.assetGenerationInlineTitle}>
                            {assetGeneration.assetType === 'video' ? '🎬 Creating Your Video' : '🎨 Creating Your Image'}
                        </div>
                        <div className="assetGenerationInlineSubtitle" style={sty.assetGenerationInlineSubtitle}>
                            {statusMessage}
                        </div>
                    </div>
                </div>

                <div className="assetGenerationInlineTime" style={sty.assetGenerationInlineTime}>
                    {formatTime(elapsedTime)}
                </div>
            </div>

            {/* Progress section */}
            <div className="assetGenerationInlineProgress" style={sty.assetGenerationInlineProgress}>
                <div className="assetGenerationInlineProgressBar" style={sty.assetGenerationInlineProgressBar}>
                    <div
                        className="assetGenerationInlineProgressFill"
                        style={styx(
                            sty.assetGenerationInlineProgressFill,
                            { width: `${progress}%` }
                        )}
                    />
                </div>
                <div className="assetGenerationInlineProgressText" style={sty.assetGenerationInlineProgressText}>
                    {progress}%
                </div>
            </div>

            {/* Status section - consolidated to avoid duplication */}
            <div className="assetGenerationInlineStatus" style={sty.assetGenerationInlineStatus}>
                <Icon
                    icon={statusInfo.icon}
                    size={14}
                    className="assetGenerationInlineStatusIcon"
                    style={styx(sty.assetGenerationInlineStatusIcon, { color: statusInfo.color })}
                />
                <span className="assetGenerationInlineStatusText" style={sty.assetGenerationInlineStatusText}>
                    {currentStatus?.status === 'generating' ? 'AI is working...' : 
                     currentStatus?.status === 'draft' ? 'Getting ready...' : 
                     'Starting up...'}
                </span>
            </div>

            {/* Estimated time remaining */}
            <div className="assetGenerationInlineEstimate" style={sty.assetGenerationInlineEstimate}>
                <Icon
                    icon="clock"
                    size={12}
                    className="assetGenerationInlineEstimateIcon"
                />
                <span className="assetGenerationInlineEstimateText" style={sty.assetGenerationInlineEstimateText}>
                    Usually takes ~{assetGeneration.estimatedTimeMinutes}
                    {assetGeneration.assetType === 'video' ? ' minutes' : ' seconds'}
                </span>
            </div>
        </div>
    )
}

export default AssetGenerationInline

const sty: Record<string, CSSProperties> = {
    // Base card styles for all asset generation states
    assetGenerationInlineCard: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        borderRadius: 12,
        border: `1px solid ${Colors.grey}`,
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        animation: 'fadeInUp 0.4s ease-out',
    },

    // In-progress state styles
    assetGenerationInlineInProgress: {
        padding: 16,
        gap: 12,
    },
    assetGenerationInlineInProgress_hover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    },

    // Completed state styles
    assetGenerationInlineCompleted: {
        padding: 0,
        cursor: 'pointer',
        maxWidth: 300,
    },
    assetGenerationInlineCompleted_hover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    },

    // Aspect ratio specific styles for completed state
    assetGenerationInlineCompleted_wide: {
        maxWidth: 400,
    },
    assetGenerationInlineCompleted_square: {
        maxWidth: 320,
    },
    assetGenerationInlineCompleted_tall: {
        maxWidth: 280,
    },

    // Video container styles
    assetGenerationInlineVideoContainer: {
        position: 'relative',
        overflow: 'hidden',
    },
    assetGenerationInlineVideoContainer_wide: {
        aspectRatio: '16/9',
    },
    assetGenerationInlineVideoContainer_square: {
        aspectRatio: '1/1',
    },
    assetGenerationInlineVideoContainer_tall: {
        aspectRatio: '9/16',
    },

    assetGenerationInlineVideoThumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'all 0.3s ease',
    },

    assetGenerationInlinePlayOverlay: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '50%',
        width: 56,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
    },

    assetGenerationInlinePlayIcon: {
        color: 'white',
        marginLeft: 3, // Slight adjustment for visual centering
    },

    assetGenerationInlineSuccessBadge: {
        position: 'absolute',
        top: 12,
        right: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: Colors.green,
        color: 'white',
        padding: '6px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },

    assetGenerationInlineSuccessIcon: {
        color: 'white',
    },

    assetGenerationInlineSuccessText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 600,
    },

    assetGenerationInlineVideoInfo: {
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    assetGenerationInlineVideoTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.textColor,
    },

    assetGenerationInlineVideoSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },

    assetGenerationInlineVideoStats: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: Colors.greyDark,
    },

    assetGenerationInlineStatsText: {
        fontSize: 12,
        color: Colors.greyDark,
    },

    // Failed state styles
    assetGenerationInlineFailed: {
        padding: 20,
        gap: 16,
        alignItems: 'center',
        textAlign: 'center',
        borderColor: Colors.red,
        backgroundColor: '#fef2f2',
    },
    assetGenerationInlineFailed_hover: {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
    },

    assetGenerationInlineFailedIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    assetGenerationInlineFailedIconSvg: {
        color: Colors.red,
    },

    assetGenerationInlineFailedInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    assetGenerationInlineFailedTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.red,
    },

    assetGenerationInlineFailedSubtitle: {
        fontSize: 14,
        color: Colors.greyDark,
    },

    // In-progress state styles
    assetGenerationInlineHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: 500,
        color: Colors.textColor,
    },
    assetGenerationInlineTitleSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flex: 1,
        minWidth: 0, // Allow text to shrink
    },
    assetGenerationInlineIcon: {
        marginRight: 6,
        flexShrink: 0,
    },
    assetGenerationInlineTitles: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1,
        minWidth: 0, // Allow text to shrink
    },
    assetGenerationInlineTitle: {
        fontWeight: 'bold',
        fontSize: 16,
    },
    assetGenerationInlineSubtitle: {
        fontSize: 14,
    },
    assetGenerationInlineTime: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.greyDark,
        flexShrink: 0,
        minWidth: 45, // Ensure timer has enough space
        textAlign: 'right',
        fontFamily: 'monospace', // Use monospace font for consistent spacing
    },
    assetGenerationInlineProgress: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    assetGenerationInlineProgressBar: {
        flex: 1,
        height: 8,
        backgroundColor: Colors.greyLight,
        borderRadius: 4,
        overflow: 'hidden',
    },
    assetGenerationInlineProgressFill: {
        height: '100%',
        backgroundColor: Colors.purple,
        transition: 'width 0.3s ease',
    },
    assetGenerationInlineProgressText: {
        fontWeight: 500,
        fontSize: 14,
        color: Colors.textColor,
        minWidth: 40,
        textAlign: 'right',
    },
    assetGenerationInlineStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    assetGenerationInlineStatusIcon: {
        flexShrink: 0,
    },
    assetGenerationInlineStatusText: {
        fontWeight: 500,
        fontSize: 14,
        color: Colors.textColor,
    },
    assetGenerationInlineEstimate: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: Colors.greyDark,
    },
    assetGenerationInlineEstimateText: {
        fontSize: 12,
        color: Colors.greyDark,
    },
}